import { WarningOutlined } from '@ant-design/icons';
import { Button, Dropdown, MenuProps, Modal, Space, Spin, Table, Tag, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { ReactElement, ReactNode, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import PermissionsEnum from '~Api/Administrator/PermissionsEnum';
import ILoan from '~Api/Loan/ILoan';
import ILoanPayoutFigure from '~Api/Loan/ILoanPayoutFigure';
import LoanPayoutFigureApprovalStatusEnum from '~Api/Loan/LoanPayoutFigureApprovalStatusEnum';
import LoanStatusEnum from '~Api/Loan/LoanStatusEnum';
import IAuthUser from '~Auth/IAuthUser';
import { authCurrentUserSelector } from '~Auth/selectors';
import {
    loanGetAction,
    loanPayoutFigureApproveAction,
    loanPayoutFigureCloneAction,
    loanPayoutFigureDeleteAction,
    loanPayoutFiguresListAction,
} from '~Loans/actions';
import {
    loanPayoutFiguresSelector,
    loanSelector,
} from '~Loans/selectors';
import { IGlobalState } from '~reducer';
import { IDictionary } from '~utilities/IDictionary';
import CreateExtensionApplicationModal from './CreateExtensionApplicationModal';
import Layout from './Layout';
import { currencyFormatter } from '~utilities/formatters';
import { ColumnType } from 'antd/lib/table';
import { applicationAssignPayoutFigureAction } from '~Applications/actions';
import WorkflowStatusEnum from '~Api/Application/WorkflowStatusEnum';
import ModalStateEnum from '~UI/ModalStateEnum';
import ProcessPayoutFigureModal from './ProcessPayoutFigureModal';

export default function PayoutFigures(): ReactElement {
    const { uuid } = useParams<{ uuid: string }>();

    const loan: ILoan = useSelector((state: IGlobalState): ILoan => loanSelector(state, uuid));
    const payoutFigures: IDictionary<ILoanPayoutFigure> = useSelector(
        (state: IGlobalState): IDictionary<ILoanPayoutFigure> => loanPayoutFiguresSelector(state, uuid),
    );
    const currentUser: IAuthUser = useSelector(authCurrentUserSelector);

    const [ createExtensionApplicationModalIsOpen, setCreateExtensionApplicationModalIsOpen ] = useState<boolean>(false);
    const [ extensionApplicationPayoutFigureUuid, setExtensionApplicationPayoutFigureUuid ] = useState<string>(null);
    const [ processPayoutFigureModalState, setProcessPayoutFigureModalState ] = useState<ModalStateEnum>(ModalStateEnum.Off);
    const [ processPayoutFigureUuid, setProcessPayoutFigureUuid ] = useState<string>(null);

    const dispatch: Dispatch = useDispatch();

    useEffect((): void => {
        if (!loan) {
            dispatch(loanGetAction(uuid));
        }
    }, [
        dispatch,
        loan,
        uuid,
    ]);

    useEffect((): void => {
        if (!payoutFigures) {
            dispatch(loanPayoutFiguresListAction(uuid));
        }
    }, [
        dispatch,
        payoutFigures,
        uuid,
    ]);

    const onClickAssignExtensionApplicationPayoutFigure: (applicationUuid: string, payoutFigureUuid: string) => void = useCallback(
        (applicationUuid: string, payoutFigureUuid: string): void => {
            Modal.confirm({
                cancelText: 'No',
                content: 'Are you sure you want to use this payout figure for the extension application?',
                okText: 'Yes',
                okType: 'primary',
                onOk: (): void => {
                    dispatch(applicationAssignPayoutFigureAction(applicationUuid, payoutFigureUuid));
                },
                title: 'Use Payout Figure for Extension Application',
            });
        },
        [
            dispatch,
        ],
    );

    const onCloseProcessPayoutFigureModal: () => void = useCallback((): void => {
        setProcessPayoutFigureModalState(ModalStateEnum.Closed);
    }, []);

    const onClickProcessPayoutFigure: (processPayoutFigureUuid: string) => void = useCallback((processPayoutFigureUuid: string): void => {
        setProcessPayoutFigureModalState(ModalStateEnum.Open);
        setProcessPayoutFigureUuid(processPayoutFigureUuid);
    }, []);

    const onClickApprove: (payoutFigureUuid: string) => void = useCallback((payoutFigureUuid: string): void => {
        Modal.confirm({
            content: `Are you sure you want to approve this draft payout figure of ${currencyFormatter.format(payoutFigures[payoutFigureUuid].balanceAmount)}?`,
            okText: 'Approve',
            onOk: (): void => {
                dispatch(loanPayoutFigureApproveAction(payoutFigureUuid));
            },
            title: 'Approve Payout Figure',
        });
    }, [
        dispatch,
        payoutFigures,
    ]);

    const onClickClone: (payoutFigureUuid: string) => void = useCallback((payoutFigureUuid: string): void => {
        Modal.confirm({
            content: 'Clone this payout figure?',
            okText: 'Clone',
            onOk: (): void => {
                dispatch(loanPayoutFigureCloneAction(payoutFigureUuid));
            },
            title: 'Clone Payout Figure',
        });
    }, [
        dispatch,
    ]);

    const onClickCreateExtensionApplication: (payoutFigureUuid: string) => void = useCallback((payoutFigureUuid: string): void => {
        setCreateExtensionApplicationModalIsOpen(true);
        setExtensionApplicationPayoutFigureUuid(payoutFigureUuid);
    }, []);

    const onCloseCreateExtensionApplicationModal: () => void = useCallback((): void => {
        setCreateExtensionApplicationModalIsOpen(false);
    }, []);

    const onClickDelete: (payoutFigureUuid: string) => void = useCallback((payoutFigureUuid: string): void => {
        Modal.confirm({
            content: `Are you sure you want to delete this payout figure of ${currencyFormatter.format(payoutFigures[payoutFigureUuid].balanceAmount)}?`,
            okText: 'Delete',
            onOk: (): void => {
                dispatch(loanPayoutFigureDeleteAction(payoutFigureUuid));
            },
            title: 'Delete Payout Figure',
        });
    }, [
        dispatch,
        payoutFigures,
    ]);

    if (!loan || !payoutFigures) {
        return (
            <Layout uuid={uuid} section='payout-figures'>
                <Typography.Title level={2}>Payout Figures</Typography.Title>
                <Spin />
            </Layout>
        );
    }

    const hasActiveExtensionApplication: boolean = loan.extensionApplication && loan.extensionApplication.closeReason === null;

    const columns: ColumnType<ILoanPayoutFigure>[] = [
        {
            dataIndex: 'code',
            title: 'Code',
            width: '15%',
        },
        {
            dataIndex: 'dischargeDate',
            render: (dischargeDate: string): ReactNode => dayjs(dischargeDate).format('DD/MM/YYYY'),
            title: 'Proposed Payout Date',
            width: '20%',
        },
        {
            render: (payoutFigure: ILoanPayoutFigure): ReactNode => {
                let payoutFigureWarning: string = null;
                const isDraft: boolean = payoutFigure.approvalStatus === LoanPayoutFigureApprovalStatusEnum.Draft;

                if (!isDraft && !!loan.paymentDateNext && dayjs(loan.paymentDateNext) < dayjs(payoutFigure.dischargeDate)) {
                    payoutFigureWarning = 'All due payments have not been made for this loan';
                } else if (loan.status !== LoanStatusEnum.ClosedObligationsMet && Math.ceil(loan.amountRemaining) !== Math.ceil(payoutFigure.principalAmount)) {
                    payoutFigureWarning = 'Amount remaining for loan does not equal payout figure principal amount';
                }

                const payoutFigureBalanceAmount: string = currencyFormatter.format(payoutFigure.balanceAmount);

                const extensionApplicationTagBlock: JSX.Element = loan.extensionApplication && loan.extensionApplication.payoutFigureUuid && loan.extensionApplication.payoutFigureUuid === payoutFigure.uuid && (
                    <Tag>Extension</Tag>
                );

                if (payoutFigureWarning) {
                    return (
                        <Tooltip overlay={payoutFigureWarning}>
                            <Space>
                                {payoutFigureBalanceAmount}
                                {extensionApplicationTagBlock}
                                <WarningOutlined />
                            </Space>
                        </Tooltip>
                    );
                }

                return (
                    <Space>
                        {payoutFigureBalanceAmount}
                        {extensionApplicationTagBlock}
                    </Space>
                );
            },
            title: 'Amount',
        },
        {
            dataIndex: 'createdTime',
            defaultSortOrder: 'descend',
            render: (createdTime: string): ReactNode => dayjs(createdTime).format('DD/MM/YYYY HH:mm'),
            sorter: (a: ILoanPayoutFigure, b: ILoanPayoutFigure): number => dayjs(a.createdTime) > dayjs(b.createdTime) ? 1 : -1,
            title: 'Created',
            width: '20%',
        },
        {
            render: (payoutFigure: ILoanPayoutFigure): ReactNode => {
                const isDraft: boolean = payoutFigure.approvalStatus === LoanPayoutFigureApprovalStatusEnum.Draft;
                const showEditAction: boolean = isDraft && loan.status !== LoanStatusEnum.ClosedObligationsMet;
                const showCloneAction: boolean = loan.status !== LoanStatusEnum.ClosedObligationsMet;
                const showApproveAction: boolean = isDraft && currentUser.permissions.includes(PermissionsEnum.LoanPayoutFigureApprove);
                const showAssignExtensionApplicationPayoutFigure: boolean = loan.extensionApplication && loan.extensionApplication.workflowStatus !== WorkflowStatusEnum.Warehoused && loan.extensionApplication.payoutFigureUuid && loan.extensionApplication.payoutFigureUuid !== payoutFigure.uuid;
                const showProcessAction: boolean = (
                    currentUser.permissions.includes(PermissionsEnum.LoanPayoutFigureProcess)
                    && !isDraft
                    && loan.status !== LoanStatusEnum.ClosedObligationsMet
                    && (
                        Math.ceil(loan.amountRemaining) === Math.ceil(payoutFigure.principalAmount)
                        || currentUser.permissions.includes(PermissionsEnum.LoanPayoutFigureForceProcess)
                    )
                    && (!loan.paymentDateNext || dayjs(loan.paymentDateNext) > dayjs(payoutFigure.dischargeDate))
                    && !hasActiveExtensionApplication
                );
                const showCreateExtensionAction: boolean = !loan.extensionApplicationUuid && currentUser.permissions.includes(PermissionsEnum.LoanExtend);
                const showDeleteAction: boolean = !loan.extensionApplication || (loan.extensionApplication && loan.extensionApplication.payoutFigureUuid && loan.extensionApplication.payoutFigureUuid !== payoutFigure.uuid) && loan.status !== LoanStatusEnum.ClosedObligationsMet;

                if (!showEditAction && !showApproveAction && !showProcessAction && !showCreateExtensionAction && !showDeleteAction) {
                    return (
                        <Button type='primary' href={`${process.env.API_HOST}/loan-payout-figures/${payoutFigure.uuid}/pdf`} target='_blank' rel='noreferrer'>View PDF</Button>
                    );
                }

                const payoutFigureActionsMenu: MenuProps = {
                    items: [
                        ...(showEditAction ? [
                            {
                                key: 'edit',
                                label: <Link to={`/loans/${loan.uuid}/payout-figures/${payoutFigure.uuid}/edit`}>Edit</Link>,
                            },
                        ] : []),
                        ...(showCloneAction ? [
                            {
                                key: 'clone',
                                label: 'Clone',
                                onClick: (): void => onClickClone(payoutFigure.uuid),
                            },
                        ] : []),
                        ...(showAssignExtensionApplicationPayoutFigure ? [
                            {
                                key: 'assign',
                                label: 'Use for Extension Application',
                                onClick: (): void => onClickAssignExtensionApplicationPayoutFigure(loan.extensionApplication.uuid, payoutFigure.uuid),
                            },
                        ] : []),
                        ...(showApproveAction ? [
                            {
                                key: 'approve',
                                label: 'Approve',
                                onClick: (): void => onClickApprove(payoutFigure.uuid),
                            },
                        ] : []),
                        ...(showProcessAction ? [
                            {
                                danger: Math.ceil(loan.amountRemaining) !== Math.ceil(payoutFigure.principalAmount),
                                key: 'process',
                                label: 'Process',
                                onClick: (): void => onClickProcessPayoutFigure(payoutFigure.uuid),
                            },
                        ] : []),
                        ...(showCreateExtensionAction ? [
                            {
                                key: 'create-extension',
                                label: 'Create Extension',
                                onClick: (): void => onClickCreateExtensionApplication(payoutFigure.uuid),
                            },
                        ] : []),
                        ...(showDeleteAction ? [
                            {
                                danger: true,
                                key: 'delete',
                                label: 'Delete',
                                onClick: (): void => onClickDelete(payoutFigure.uuid),
                            },
                        ] : []),
                    ],
                };

                const createExtensionApplicationModalBlock: JSX.Element = payoutFigure.uuid === extensionApplicationPayoutFigureUuid && (
                    <CreateExtensionApplicationModal
                        isOpen={createExtensionApplicationModalIsOpen}
                        onClose={onCloseCreateExtensionApplicationModal}
                        loanPayoutFigureUuid={payoutFigure.uuid}
                    />
                );

                const processPayoutFigureModalBlock: JSX.Element = payoutFigure.uuid === processPayoutFigureUuid && (
                    <ProcessPayoutFigureModal
                        isOpen={processPayoutFigureModalState}
                        onCancel={onCloseProcessPayoutFigureModal}
                        payoutFigureUuid={payoutFigure.uuid}
                    />
                );

                const viewPdfBlock: JSX.Element = payoutFigureActionsMenu.items.length > 0 ? (
                    <Dropdown.Button menu={payoutFigureActionsMenu} type='primary'>
                        <a href={`${process.env.API_HOST}/loan-payout-figures/${payoutFigure.uuid}/pdf`} target='_blank' rel='noreferrer'>View PDF</a>
                    </Dropdown.Button>
                ) : (
                    <Button>
                        <a href={`${process.env.API_HOST}/loan-payout-figures/${payoutFigure.uuid}/pdf`} target='_blank' rel='noreferrer'>View PDF</a>
                    </Button>
                );

                return (
                    <Space>
                        {viewPdfBlock}
                        {createExtensionApplicationModalBlock}
                        {processPayoutFigureModalBlock}
                    </Space>
                );
            },
            width: '15%',
        },
    ];

    return (
        <Layout uuid={uuid} section='payout-figures'>
            <Space className='actions'>
                <Link to={`/loans/${loan.uuid}/payout-figures/add`}><Button className='create'>Create Payout Figure</Button></Link>
            </Space>
            <Typography.Title level={2}>Payout Figures</Typography.Title>
            <Table
                columns={columns}
                dataSource={_.values(payoutFigures)}
                rowKey='uuid'
                pagination={false}
                size='middle'
            />
        </Layout>
    );
}
